"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Context to share drawer direction across components.
 */
const DrawerContext = React.createContext<{
  direction?: "right" | "top" | "bottom" | "left";
}>({
  direction: "right",
});

/**
 * Drawer
 *
 * A responsive sliding panel for displaying additional content.
 * Supports directions: `top`, `bottom`, `left`, `right`.
 */
const Drawer = ({
  shouldScaleBackground = true,
  direction = "right",
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  const contextValue = React.useMemo(() => ({ direction }), [direction]);

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        direction={direction}
        {...props}
      />
    </DrawerContext.Provider>
  );
};

Drawer.displayName = "Drawer";

// Drawer trigger button or element.
const DrawerTrigger = DrawerPrimitive.Trigger;

// Drawer portal for rendering overlay and content.
const DrawerPortal = DrawerPrimitive.Portal;

//Closes the drawer when triggered.
const DrawerClose = DrawerPrimitive.Close;

/**
 * DrawerOverlay
 *
 * Semi-transparent background overlay shown behind the drawer.
 */
const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/80", className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

// Style variants for drawer placement.
const drawerContentVariants = cva(
  "fixed z-50 flex h-auto flex-col bg-background",
  {
    variants: {
      direction: {
        right: "ml-24 right-0 inset-y-0",
        top: "mb-24 top-0 inset-x-0",
        bottom: "mt-24 bottom-0 inset-x-0",
        left: "mr-24 left-0 inset-y-0",
      },
    },
    defaultVariants: {
      direction: "right",
    },
  },
);

/**
 * DrawerContent
 *
 * The main container for drawer content.
 * Automatically adapts to direction (top, right, bottom, left).
 */
const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content> ,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    showOverlay?: boolean;
  }
>(({ className, children = true, showOverlay = true, ...props }, ref) => {
  const { direction } = React.useContext(DrawerContext);

  return (
    <DrawerPortal>
      {showOverlay && <DrawerOverlay />}
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(drawerContentVariants({ direction, className }))}
        {...props}
      >
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
};
